import { Builder, BuilderElement } from "@builder.io/react";
import { ProductTileType } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { Surface } from "@faire/web-api--source/com/faire/logging/Surface";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";

import { BuilderRetailerProductTilesSection } from "./BuilderRetailerProductTileSection";

export const registerBuilderRetailerProductTileSection = () => {
  Builder.registerComponent(BuilderRetailerProductTilesSection, {
    name: "RetailerProductTileSection",
    hideFromInsertMenu: false,
    noWrap: true,
    canHaveChildren: false,
    inputs: [
      {
        name: "products",
        type: "list",
        helperText: "Duplicate products will be automatically removed.",
        subFields: [
          {
            name: "tokenProd",
            friendlyName: "Production Product Token",
            type: "string",
            regex: {
              // eslint-disable-next-line @faire/no-dollar-symbol-in-strings
              pattern: "^p_[a-zA-Z0-9]{8,10}$",
              message:
                "Product token must be in the format of `p_xxxxxxxxxx` consisting of alphanumeric characters",
            },
            required: true,
          },
          {
            name: "tokenStaging",
            friendlyName: "Staging Product Token",
            type: "string",
            regex: {
              // eslint-disable-next-line @faire/no-dollar-symbol-in-strings
              pattern: "^p_[a-zA-Z0-9]{8,10}$",
              message:
                "Product token must be in the format of `p_xxxxxxxxxx` consisting of alphanumeric characters",
            },
            required: false,
            // @ts-expect-error: Hack to access fields outside of the subfields array
            showIf: (_, __, parentElements: BuilderElement[]) =>
              parentElements
                .find(
                  (element: BuilderElement) =>
                    element?.component?.name === "RetailerProductTileSection"
                )
                ?.component?.options.get("useCustomStagingToken"),
          },
          {
            name: "type",
            friendlyName: "Product Tile Type",
            type: "enum",
            defaultValue: ProductTileType.TILE_WITHOUT_BRAND,
            enum: Object.values(ProductTileType),
            required: true,
          },
        ],
        required: true,
      },
      {
        name: "columns",
        friendlyName: "Number of Columns",
        type: "object",
        defaultValue: {
          mobileAndAbove: 2,
          tabletAndAbove: 2,
          desktopAndAbove: 2,
          xLargeAndAbove: 2,
          xxLargeDesktop: 2,
        },
        subFields: [
          {
            name: "mobileAndAbove",
            type: "number",
            defaultValue: 2,
          },
          {
            name: "tabletAndAbove",
            type: "number",
            defaultValue: 2,
          },
          {
            name: "desktopAndAbove",
            type: "number",
            defaultValue: 2,
          },
          {
            name: "xLargeAndAbove",
            type: "number",
            defaultValue: 2,
          },
          {
            name: "xxLargeDesktop",
            type: "number",
            defaultValue: 2,
          },
        ],
      },
      {
        name: "rowGap",
        friendlyName: "Row Gap (px)",
        type: "object",
        defaultValue: {
          mobileAndAbove: 8,
          tabletAndAbove: 8,
          desktopAndAbove: 8,
          xLargeAndAbove: 8,
          xxLargeDesktop: 8,
        },
        subFields: [
          {
            name: "mobileAndAbove",
            type: "number",
            defaultValue: 8,
          },
          {
            name: "tabletAndAbove",
            type: "number",
            defaultValue: 8,
          },
          {
            name: "desktopAndAbove",
            type: "number",
            defaultValue: 8,
          },
          {
            name: "xLargeAndAbove",
            type: "number",
            defaultValue: 8,
          },
          {
            name: "xxLargeDesktop",
            type: "number",
            defaultValue: 8,
          },
        ],
      },
      {
        name: "columnGap",
        friendlyName: "Column Gap (px)",
        type: "object",
        defaultValue: {
          mobileAndAbove: 8,
          tabletAndAbove: 8,
          desktopAndAbove: 8,
          xLargeAndAbove: 8,
          xxLargeDesktop: 8,
        },
        subFields: [
          {
            name: "mobileAndAbove",
            type: "number",
            defaultValue: 8,
          },
          {
            name: "tabletAndAbove",
            type: "number",
            defaultValue: 8,
          },
          {
            name: "desktopAndAbove",
            type: "number",
            defaultValue: 8,
          },
          {
            name: "xLargeAndAbove",
            type: "number",
            defaultValue: 8,
          },
          {
            name: "xxLargeDesktop",
            type: "number",
            defaultValue: 8,
          },
        ],
      },
      {
        name: "useCustomStagingToken",
        advanced: true,
        friendlyName: "Use Custom Staging Token",
        helperText:
          "Provide real staging tokens in place of mock tiles. Enter staging tokens in each product entry.",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "loggingContext",
        advanced: true,
        friendlyName: "Event Logging Context",
        type: "object",
        subFields: [
          {
            name: "productTileContainer",
            type: "enum",
            enum: Object.values(ProductTileContainer),
          },
          {
            name: "screenContext",
            type: "enum",
            enum: Object.values(Surface),
          },
          {
            name: "containerContext",
            type: "enum",
            enum: Object.values(ProductTileScreenContainer),
          },
          {
            name: "contextToken",
            type: "string",
          },
          {
            name: "trackedLinkContainerName",
            type: "string",
          },
          {
            name: "isNewProductsCategory",
            type: "boolean",
          },
        ],
      },
    ],
  });
};
